import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import { IconTrash, IconPlus } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import debounce from 'lodash.debounce';
import api from '../../../../services/api';

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const CreateSalesQuote = () => {
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });

    const [orgSearch, setOrgSearch] = useState('');
    const [orgResults, setOrgResults] = useState([]);
    const [showOrgResults, setShowOrgResults] = useState(false);
    const [organization, setOrganization] = useState(null);

    const [validUntil, setValidUntil] = useState('');
    const [notes, setNotes] = useState('');

    const [products, setProducts] = useState([]);
    const [services, setServices] = useState([]);
    const [items, setItems] = useState([]);

    const [itemType, setItemType] = useState('produto');
    const [selectedProductId, setSelectedProductId] = useState('');
    const [selectedVariacaoId, setSelectedVariacaoId] = useState('');
    const [selectedServiceId, setSelectedServiceId] = useState('');
    const [quantidade, setQuantidade] = useState(1);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            const res = await api.get(`v1/apps/${appId}/products/`);
            setProducts(res.data || []);
        })();
    }, [appId]);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            const res = await api.get(`v1/apps/${appId}/services/`);
            setServices(res.data.services || []);
        })();
    }, [appId]);

    const handleOrgSearch = useMemo(
        () =>
            debounce(async (value, currentAppId) => {
                if (!value || !currentAppId) {
                    setOrgResults([]);
                    setShowOrgResults(false);
                    return;
                }
                const res = await api.get(`v1/apps/${currentAppId}/organizations/?search=${encodeURIComponent(value)}`);
                setOrgResults(res.data.organizations || []);
                setShowOrgResults(true);
            }, 400),
        []
    );

    const handleOrgSearchChange = (e) => {
        const value = e.target.value;
        setOrgSearch(value);
        setOrganization(null);
        handleOrgSearch(value, appId);
    };

    const selectOrganization = (org) => {
        setOrganization(org);
        setOrgSearch(org.name);
        setShowOrgResults(false);
    };

    const selectedProduct = products.find((p) => String(p.id) === String(selectedProductId));
    const variacoesDoProduto = selectedProduct?.variacoes || [];
    const selectedVariacao = variacoesDoProduto.find((v) => String(v.id) === String(selectedVariacaoId));
    const selectedService = services.find((s) => String(s.id) === String(selectedServiceId));

    const addItem = () => {
        const qtd = Number(quantidade);
        if (!qtd || qtd <= 0) {
            setStatus({ type: 'error', message: 'Quantidade deve ser maior que zero' });
            return;
        }

        if (itemType === 'produto') {
            if (!selectedVariacao) {
                setStatus({ type: 'error', message: 'Selecione um produto e uma variação' });
                return;
            }
            setItems((prev) => [
                ...prev,
                {
                    produto_variacao: selectedVariacao.id,
                    service: null,
                    nome: `${selectedProduct.nome} - ${selectedVariacao.nome_variacao}`,
                    quantidade: qtd,
                    valor_unitario: selectedVariacao.valor_venda
                }
            ]);
            setSelectedProductId('');
            setSelectedVariacaoId('');
        } else {
            if (!selectedService) {
                setStatus({ type: 'error', message: 'Selecione um serviço' });
                return;
            }
            setItems((prev) => [
                ...prev,
                {
                    produto_variacao: null,
                    service: selectedService.id,
                    nome: selectedService.name,
                    quantidade: qtd,
                    valor_unitario: selectedService.price
                }
            ]);
            setSelectedServiceId('');
        }

        setQuantidade(1);
        setStatus({ type: '', message: '' });
    };

    const removeItem = (index) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    const total = items.reduce((acc, item) => acc + Number(item.quantidade) * Number(item.valor_unitario), 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: '', message: '' });

        if (!organization) {
            setStatus({ type: 'error', message: 'Selecione um cliente' });
            return;
        }
        if (items.length === 0) {
            setStatus({ type: 'error', message: 'Adicione ao menos um item ao orçamento' });
            return;
        }

        try {
            const payload = {
                organization: organization.id,
                valid_until: validUntil || undefined,
                notes,
                items: items.map((item) => ({
                    produto_variacao: item.produto_variacao || undefined,
                    service: item.service || undefined,
                    quantidade: item.quantidade,
                    valor_unitario: item.valor_unitario
                }))
            };
            const response = await api.post(`v1/apps/${appId}/sales_quotes/`, payload);
            history.push(`/dashboard/sales/quotes/${response.data.id}/`);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao criar orçamento' });
        }
    };

    return (
        <Box>
            <Typography variant="h3" gutterBottom sx={{ mb: 2 }}>
                Novo Orçamento
            </Typography>

            <Card>
                <CardContent>
                    {status.message && (
                        <Alert severity={status.type} sx={{ mb: 2 }}>
                            {status.message}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} sx={{ position: 'relative' }}>
                                <TextField
                                    label="Cliente"
                                    fullWidth
                                    required
                                    value={orgSearch}
                                    onChange={handleOrgSearchChange}
                                    onFocus={() => orgResults.length > 0 && setShowOrgResults(true)}
                                    placeholder="Buscar por nome ou CNPJ..."
                                />
                                {showOrgResults && orgResults.length > 0 && (
                                    <Paper sx={{ position: 'absolute', zIndex: 10, width: '100%', maxHeight: 240, overflowY: 'auto' }}>
                                        <List dense>
                                            {orgResults.map((org) => (
                                                <ListItem key={org.id} disablePadding>
                                                    <ListItemButton onClick={() => selectOrganization(org)}>
                                                        {org.name} {org.cnpj ? `- ${org.cnpj}` : ''}
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Paper>
                                )}
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Válido até (opcional)"
                                    type="date"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={validUntil}
                                    onChange={(e) => setValidUntil(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Observações"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
                                    Itens
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    value={itemType}
                                    onChange={(e, value) => {
                                        if (!value) return;
                                        setItemType(value);
                                        setSelectedProductId('');
                                        setSelectedVariacaoId('');
                                        setSelectedServiceId('');
                                    }}
                                >
                                    <ToggleButton value="produto">Produto</ToggleButton>
                                    <ToggleButton value="servico">Serviço</ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>

                            {itemType === 'produto' ? (
                                <>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            select
                                            label="Produto"
                                            fullWidth
                                            value={selectedProductId}
                                            onChange={(e) => {
                                                setSelectedProductId(e.target.value);
                                                setSelectedVariacaoId('');
                                            }}
                                        >
                                            {products.map((p) => (
                                                <MenuItem key={p.id} value={p.id}>
                                                    {p.nome}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            select
                                            label="Variação"
                                            fullWidth
                                            disabled={!selectedProductId}
                                            value={selectedVariacaoId}
                                            onChange={(e) => setSelectedVariacaoId(e.target.value)}
                                        >
                                            {variacoesDoProduto.map((v) => (
                                                <MenuItem key={v.id} value={v.id}>
                                                    {v.nome_variacao} - {formatCurrencyBR(v.valor_venda)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </>
                            ) : (
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        select
                                        label="Serviço"
                                        fullWidth
                                        value={selectedServiceId}
                                        onChange={(e) => setSelectedServiceId(e.target.value)}
                                    >
                                        {services.map((s) => (
                                            <MenuItem key={s.id} value={s.id}>
                                                {s.name} - {formatCurrencyBR(s.price)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            )}

                            <Grid item xs={8} sm={2}>
                                <TextField
                                    label="Qtd"
                                    type="number"
                                    fullWidth
                                    inputProps={{ min: 1 }}
                                    value={quantidade}
                                    onChange={(e) => setQuantidade(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={4} sm={2} display="flex" alignItems="center">
                                <Button variant="outlined" startIcon={<IconPlus size={18} />} onClick={addItem} fullWidth>
                                    Adicionar
                                </Button>
                            </Grid>

                            <Grid item xs={12}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell align="right">Quantidade</TableCell>
                                            <TableCell align="right">Valor unitário</TableCell>
                                            <TableCell align="right">Subtotal</TableCell>
                                            <TableCell align="right" />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{item.nome}</TableCell>
                                                <TableCell align="right">{item.quantidade}</TableCell>
                                                <TableCell align="right">{formatCurrencyBR(item.valor_unitario)}</TableCell>
                                                <TableCell align="right">
                                                    {formatCurrencyBR(item.quantidade * item.valor_unitario)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" onClick={() => removeItem(index)}>
                                                        <IconTrash size={16} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {items.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    Nenhum item adicionado
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Grid>

                            <Grid item xs={12} textAlign="right">
                                <Typography variant="h5">Total: {formatCurrencyBR(total)}</Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                    <Button onClick={() => history.push('/dashboard/sales/quotes')}>Voltar</Button>
                                    <Button type="submit" variant="contained">
                                        Salvar Orçamento (Rascunho)
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CreateSalesQuote;
